export const getOidcStorageKey = () => {
  const authSettings = JSON.parse(localStorage.getItem("authSettings"));
  if (authSettings) {
    return `oidc.user:${authSettings.auth_server}:${authSettings.client_id}`;
  }
  return null;
};

export const getOidcInfo = () => {
  const key = getOidcStorageKey();
  if (key) {
    const oidc = JSON.parse(localStorage.getItem(key));
    return oidc;
  }
  return null;
};

export const getToken = () => {
  const oidc = getOidcInfo();
  if (oidc) {
    return oidc.id_token;
  }
  return null;
};

const getDay = (date) => {
  if (!date) return "";
  return `${new Date(date)?.getDate()}`;
};

const getValue = (price) => {
  if (Number.isNaN(price)) return 0;
  return Number(price).toFixed(2);
};

const getNameAndValueArr = (history) =>
  history.map((day) => ({
    name: getDay(day?.date),
    value: getValue(day.priceUsd),
  }));

export const getWeekChartData = (cryptoHistory) => {
  if (!cryptoHistory) return null;

  const weekChartDataArr = cryptoHistory?.map((item) => ({
    currency: item?.currency,
    history: getNameAndValueArr(item?.history.slice(7, item?.history.length)),
  }));

  return Object.fromEntries(
    weekChartDataArr.map((item) => [item.currency, item.history])
  );
};

export const getShortCryptoAddress = (value) =>
  `${value?.slice(0, 10)}...${value?.slice(-3)}`;
export const getNetworkName = (chainId) => {
  const chainIdToNetworkMap = {
    97: "Binance Smart Chain Testnet",
    80001: "Polygon Mumbai Testnet",
    321: "Bitcoin Testnet",
    123: "Solana Testnet",
    11155111: "Ethereum Sepolia Testnet",
    111: "Tron Testnet",
    56: "Binance Smart Chain Mainnet",
    137: "Polygon Mainnet",
    1: "Ethereum Mainnet",
    321: "Bitcoin Mainnet",
    123: "Solana Mainnet",
    111: "Tron Mainnet",
  };
  return chainIdToNetworkMap[chainId] || "Unknown Network";
};

export const formatNumberWithSubscript = (value, maxDecimals = 8) => {
  if (value === null || value === undefined || isNaN(value)) {
    return { formatted: "0", parts: null, useSubscript: false };
  }

  const num = Number(value);
  
  // If number is 0 or very close to 0
  if (num === 0) {
    return { formatted: "0", parts: null, useSubscript: false };
  }

  // If number is >= 1, use normal formatting
  if (num >= 1) {
    return { 
      formatted: num.toLocaleString(undefined, { maximumFractionDigits: maxDecimals }),
      parts: null,
      useSubscript: false
    };
  }

  // Convert to string with high precision to count leading zeros
  const numStr = num.toFixed(maxDecimals);
  
  // Remove trailing zeros but keep structure
  let cleanStr = numStr.replace(/\.?0+$/, '');
  
  // Check if there are leading zeros after decimal point
  const decimalIndex = cleanStr.indexOf('.');
  if (decimalIndex === -1) {
    return { formatted: cleanStr, parts: null, useSubscript: false };
  }

  const afterDecimal = cleanStr.substring(decimalIndex + 1);
  
  // Count leading zeros after decimal point
  let leadingZeros = 0;
  for (let i = 0; i < afterDecimal.length; i++) {
    if (afterDecimal[i] === '0') {
      leadingZeros++;
    } else {
      break;
    }
  }

  // If we have 3 or more leading zeros, use subscript notation
  if (leadingZeros >= 3) {
    const significantDigits = afterDecimal.substring(leadingZeros);
    
    // Subscript number mapping (0-9)
    const subscriptMap = {
      '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
      '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
    };
    
    const subscriptStr = leadingZeros.toString().split('').map(d => subscriptMap[d] || d).join('');
    
    return {
      formatted: `0.0${subscriptStr}${significantDigits}`,
      parts: {
        prefix: "0.0",
        subscript: subscriptStr,
        significant: significantDigits,
        fullValue: cleanStr // Keep original for hover/display
      },
      useSubscript: true
    };
  }

  // Otherwise, return normal formatting
  return { 
    formatted: cleanStr,
    parts: null,
    useSubscript: false
  };
};

export const formatCurrencyWithSubscript = (value, symbol = "$", maxDecimals = 8) => {
  const result = formatNumberWithSubscript(value, maxDecimals);
  
  if (result.useSubscript) {
    return {
      formatted: `${symbol}${result.formatted}`,
      parts: {
        symbol,
        ...result.parts
      },
      useSubscript: true
    };
  }
  
  return {
    formatted: `${symbol}${result.formatted}`,
    parts: null,
    useSubscript: false
  };
};
